import { useState } from "react";
import { Building2, Check, ChevronRight, LogOut, Mail, ShieldCheck, X } from "lucide-react";
import { Button } from "../../components/Button";
import { MessageBanner } from "../../components/MessageBanner";
import { allowedTenants, roleLabel, type MobileRole } from "../../lib/access";
import { useSession } from "../../lib/session/SessionContext";

interface ProfileTabProps {
  readonly role: MobileRole;
  readonly ctName: string;
}

export function ProfileTab({ role, ctName }: ProfileTabProps) {
  const { userEmail, tenants, activeTenantId, switchTenant, logout, error } = useSession();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const selectable = allowedTenants(tenants);
  const initial = userEmail?.trim().charAt(0).toUpperCase() || "A";

  const pick = async (tenantId: string) => {
    if (tenantId === activeTenantId) {
      setSheetOpen(false);
      return;
    }
    setBusyId(tenantId);
    await switchTenant(tenantId);
    setBusyId(null);
    setSheetOpen(false);
  };

  return (
    <div className="tab-page">
      <section className="profile-head">
        <span className="avatar avatar--large" aria-hidden="true">{initial}</span>
        <h1 className="profile-head__name">{userEmail ?? "Conta Apogeu"}</h1>
        <span className="role-pill">{roleLabel(role)}</span>
      </section>

      <section className="section">
        <div className="section__heading">
          <h2 className="section__title">Sua conta</h2>
        </div>
        <div className="list-card">
          <div className="list-row">
            <span className="list-row__icon"><Mail size={18} aria-hidden="true" /></span>
            <div className="list-row__body">
              <span className="list-row__label">E-mail</span>
              <strong className="list-row__value">{userEmail ?? "—"}</strong>
            </div>
          </div>
          <div className="list-row">
            <span className="list-row__icon"><ShieldCheck size={18} aria-hidden="true" /></span>
            <div className="list-row__body">
              <span className="list-row__label">Perfil no app</span>
              <strong className="list-row__value">{roleLabel(role)}</strong>
            </div>
          </div>
          <div className="list-row">
            <span className="list-row__icon"><Building2 size={18} aria-hidden="true" /></span>
            <div className="list-row__body">
              <span className="list-row__label">Centro de treinamento</span>
              <strong className="list-row__value">{ctName}</strong>
            </div>
            {selectable.length > 1 && (
              <button type="button" className="list-row__action" onClick={() => setSheetOpen(true)}>
                Trocar <ChevronRight size={16} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      </section>

      {error && <MessageBanner>{error}</MessageBanner>}

      <section className="section">
        <Button variant="outline" fullWidth onClick={() => setSheetOpen(true)} disabled={selectable.length <= 1}>
          <Building2 size={16} aria-hidden="true" />
          Trocar de CT
        </Button>
        <Button variant="danger" fullWidth onClick={() => void logout()}>
          <LogOut size={16} aria-hidden="true" />
          Sair da conta
        </Button>
      </section>

      <p className="profile-foot">Apogeu App · versão 0.1.0</p>

      {sheetOpen && (
        <div className="sheet-layer" role="dialog" aria-modal="true" aria-label="Trocar de CT">
          <button type="button" className="sheet-backdrop" aria-label="Fechar" onClick={() => setSheetOpen(false)} />
          <div className="sheet">
            <div className="sheet__handle" aria-hidden="true" />
            <div className="sheet__header">
              <div>
                <h2 className="sheet__title">Trocar de CT</h2>
                <p className="sheet__subtitle">Escolha onde quer entrar agora.</p>
              </div>
              <button type="button" className="sheet__close" onClick={() => setSheetOpen(false)} aria-label="Fechar">
                <X size={20} />
              </button>
            </div>
            <div className="tenant-list">
              {selectable.map(tenant => (
                <button
                  key={tenant.tenantId}
                  type="button"
                  className={`tenant-option ${tenant.tenantId === activeTenantId ? "tenant-option--active" : ""}`.trim()}
                  onClick={() => void pick(tenant.tenantId)}
                  disabled={busyId !== null}
                >
                  <span className="tenant-option__icon"><Building2 size={19} aria-hidden="true" /></span>
                  <span className="tenant-option__body">
                    <span className="tenant-option__name">{tenant.name}</span>
                    <span className="tenant-option__role">{roleLabel(role)}</span>
                  </span>
                  {tenant.tenantId === activeTenantId && <Check size={18} color="#7FA200" aria-hidden="true" />}
                  {busyId === tenant.tenantId && <span className="tenant-option__busy">Entrando...</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
